// src/pages/AdminPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, Table, Button, Modal, Form, Input, InputNumber, DatePicker, 
  Select, message, Typography, Space, Row, Col, Tag, Popconfirm,
  Avatar, Tabs, Descriptions
} from 'antd';
import { 
  UserOutlined, PlusOutlined, EditOutlined, DeleteOutlined, 
  ExclamationCircleOutlined, DownloadOutlined, SearchOutlined,
  KeyOutlined, TeamOutlined, GiftOutlined
} from '@ant-design/icons';
import { useAuth } from '../App';
import { adminAPI } from '../utils/api';
import { DoctorPublicIdCard } from '../components/DoctorPublicIdCard';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { joinFullName, splitFullName } from '../utils/fullName';
import { getFilePreviewUrl } from '../utils/fileUrls';

const { Title, Text } = Typography;
const { Option } = Select;

interface User {
  id: number;
  email: string;
  full_name: string;
  phone: string;
  role: string;
  week_of_pregnancy?: number;
  due_date?: string;
  doctor_id?: number;
  is_active: boolean;
  telegram_username?: string;
  avatar_url?: string;
  specialization?: string;
  license_number?: string;
  clinic_id?: number;
  doctor_public_id?: string;
}

interface Clinic {
  id: number;
  name: string;
  city?: string;
  slug: string;
  contact_phone?: string;
  contact_email?: string;
}

export const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [patientsModalVisible, setPatientsModalVisible] = useState(false);
  const [actionsModalVisible, setActionsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedDoctor, setSelectedDoctor] = useState<User | null>(null);
  const [doctorPatients, setDoctorPatients] = useState<User[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [butterflyModalVisible, setButterflyModalVisible] = useState(false);
  const [butterflyUsers, setButterflyUsers] = useState<any[]>([]);
  const [loadingButterflyUsers, setLoadingButterflyUsers] = useState(false);
  const [butterflyForm] = Form.useForm();
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [assignForm] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string | undefined>(undefined);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [clinicModalVisible, setClinicModalVisible] = useState(false);
  const [editingClinic, setEditingClinic] = useState<Clinic | null>(null);
  const [clinicForm] = Form.useForm();

  const loadClinics = useCallback(async () => {
    try {
      const response = await adminAPI.getClinics();
      setClinics((response.data as Clinic[]) || []);
    } catch {
      message.error('Не удалось загрузить список клиник');
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (roleFilter) params.role = roleFilter;
      if (searchTerm) params.q = searchTerm;
      const response = await adminAPI.getUsers(params);
      setUsers(response.data as User[]);
    } catch (error: any) {
      message.error('Ошибка загрузки пользователей');
    } finally {
      setLoading(false);
    }
  }, [roleFilter, searchTerm]);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadUsers();
      loadClinics();
    }
  }, [user, loadUsers, loadClinics]);

  const handleCreateUser = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleRowClick = (record: User) => {
    setSelectedUser(record);
    setActionsModalVisible(true);
  };

  const handleEditUser = (record?: User) => {
    const userToEdit = record || selectedUser;
    if (!userToEdit) return;
    setEditingUser(userToEdit);
    const nameParts = splitFullName(userToEdit.full_name);
    form.setFieldsValue({
      ...userToEdit,
      last_name: nameParts.lastName,
      first_name: nameParts.firstName,
      middle_name: nameParts.middleName,
      due_date: userToEdit.due_date ? dayjs(userToEdit.due_date) : null,
      password: undefined, // Don't show password
    });
    setActionsModalVisible(false);
    setModalVisible(true);
  };

  const handleResetPassword = (record?: User) => {
    const userToReset = record || selectedUser;
    if (!userToReset) return;
    setEditingUser(userToReset);
    passwordForm.resetFields();
    setActionsModalVisible(false);
    setPasswordModalVisible(true);
  };

  const handleDeleteUser = async (userId: number) => {
    try {
      console.log('Deleting user:', userId);
      await adminAPI.deleteUser(userId);
      message.success('Пользователь деактивирован');
      loadUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Ошибка удаления пользователя';
      message.error(errorMessage);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingUser) {
        // Update user
        const updateData: any = {
          email: values.email,
          phone: values.phone,
          full_name: joinFullName({
            lastName: values.last_name,
            firstName: values.first_name,
            middleName: values.middle_name,
          }),
          telegram_username: values.telegram_username,
          week_of_pregnancy: values.week_of_pregnancy,
          due_date: values.due_date ? values.due_date.format('YYYY-MM-DD') : undefined,
          specialization: values.specialization,
          license_number: values.license_number,
          clinic_id: values.clinic_id,
        };
        // Add password only if provided
        if (values.password && values.password.trim()) {
          updateData.password = values.password;
        }
        console.log('Updating user:', editingUser.id, updateData);
        await adminAPI.updateUser(editingUser.id, updateData);
        if (editingUser.role === 'doctor' && values.clinic_id && !editingUser.doctor_public_id) {
          message.info('После назначения клиники публичный DR-ID будет сгенерирован автоматически');
        }
        message.success('Пользователь обновлен');
        setModalVisible(false);
        form.resetFields();
        loadUsers();
      } else {
        if (values.role === 'doctor' && !values.clinic_id) {
          message.error('Для врача выберите клинику');
          return;
        }
        const userData = {
          email: values.email,
          password: values.password,
          phone: values.phone,
          full_name: joinFullName({
            lastName: values.last_name,
            firstName: values.first_name,
            middleName: values.middle_name,
          }),
          role: values.role,
          telegram_username: values.telegram_username,
          week_of_pregnancy: values.week_of_pregnancy,
          due_date: values.due_date ? values.due_date.format('YYYY-MM-DD') : null,
          specialization: values.specialization,
          license_number: values.license_number,
          clinic_id: values.clinic_id,
        };
        console.log('Creating user:', userData);
        await adminAPI.createUser(userData);
        message.success('Пользователь создан');
        setModalVisible(false);
        form.resetFields();
        loadUsers();
      }
    } catch (error: any) {
      console.error('Error saving user:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Ошибка сохранения';
      message.error(errorMessage);
    }
  };

  const handlePasswordReset = async (values: any) => {
    try {
      if (!editingUser) return;
      console.log('Resetting password for user:', editingUser.id);
      await adminAPI.resetPassword(editingUser.id, values.new_password);
      message.success('Пароль успешно изменен');
      setPasswordModalVisible(false);
      passwordForm.resetFields();
      setEditingUser(null);
    } catch (error: any) {
      console.error('Error resetting password:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Ошибка сброса пароля';
      message.error(errorMessage);
    }
  };

  const handleAssignPatient = (doctor: User) => {
    setSelectedDoctor(doctor);
    assignForm.resetFields();
    setAssignModalVisible(true);
  };

  const handleAssignSubmit = async (values: any) => {
    try {
      if (!selectedDoctor) return;
      const patient = users.find((u) => u.id === values.patient_id);
      if (patient?.doctor_id && patient.doctor_id !== selectedDoctor.id) {
        message.error('У пациентки уже есть врач. Сначала отвяжите текущего врача.');
        return;
      }
      await adminAPI.assignPatientToDoctor(selectedDoctor.id, values.patient_id);
      message.success('Пациент успешно назначен врачу');
      setAssignModalVisible(false);
      assignForm.resetFields();
      loadUsers();
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Ошибка назначения пациента');
    }
  };

  const handleForceUnlinkPatient = async (patientId: number) => {
    try {
      await adminAPI.forceRevokePatientDoctorLink(patientId);
      message.success('Врач отвязан от пациентки');
      await loadUsers();
      if (selectedUser?.id === patientId) {
        const refreshed = await adminAPI.getUserDetails(patientId);
        setSelectedUser(refreshed.data as User);
      }
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Ошибка отвязки врача');
    }
  };

  const handleUnassignPatient = async (doctorId: number, patientId: number) => {
    try {
      await adminAPI.unassignPatientFromDoctor(doctorId, patientId);
      message.success('Назначение удалено');
      loadUsers();
      // Reload patients if modal is open
      if (selectedDoctor) {
        loadDoctorPatients(selectedDoctor.id);
      }
    } catch (error: any) {
      message.error('Ошибка удаления назначения');
    }
  };

  const handleViewDoctorPatients = async (doctor: User) => {
    setSelectedDoctor(doctor);
    setPatientsModalVisible(true);
    await loadDoctorPatients(doctor.id);
  };

  const loadDoctorPatients = async (doctorId: number) => {
    try {
      setLoadingPatients(true);
      const response = await adminAPI.getDoctorPatients(doctorId);
      setDoctorPatients(response.data as User[]);
    } catch (error: any) {
      message.error('Ошибка загрузки пациентов врача');
    } finally {
      setLoadingPatients(false);
    }
  };

  const loadButterflyUsers = async () => {
    try {
      setLoadingButterflyUsers(true);
      const response = await adminAPI.getUsersForGamification();
      setButterflyUsers((response.data as any).users || []);
    } catch (error: any) {
      message.error('Ошибка загрузки пользователей');
    } finally {
      setLoadingButterflyUsers(false);
    }
  };

  const openCreateClinicModal = () => {
    setEditingClinic(null);
    clinicForm.resetFields();
    setClinicModalVisible(true);
  };

  const openEditClinicModal = (clinic: Clinic) => {
    setEditingClinic(clinic);
    clinicForm.setFieldsValue({
      name: clinic.name,
      slug: clinic.slug,
      city: clinic.city || undefined,
      contact_phone: clinic.contact_phone || undefined,
      contact_email: clinic.contact_email || undefined,
    });
    setClinicModalVisible(true);
  };

  const handleSaveClinic = async (values: {
    name: string;
    slug?: string;
    city?: string;
    contact_phone?: string;
    contact_email?: string;
  }) => {
    try {
      const payload: any = {
        name: values.name.trim(),
        city: values.city || null,
        contact_phone: values.contact_phone || null,
        contact_email: values.contact_email || null,
      };
      if (values.slug?.trim()) {
        payload.slug = values.slug.trim().toUpperCase();
      }

      if (editingClinic) {
        if (!payload.slug) {
          message.error('Укажите код клиники');
          return;
        }
        const response = await adminAPI.updateClinic(editingClinic.id, payload);
        const updated = response.data as Clinic;
        message.success(`Клиника обновлена (ID ${updated.id}, код ${updated.slug})`);
      } else {
        const response = await adminAPI.createClinic(payload);
        const created = response.data as Clinic;
        message.success(
          created?.slug
            ? `Клиника добавлена (ID ${created.id}, код ${created.slug})`
            : 'Клиника добавлена'
        );
      }
      setClinicModalVisible(false);
      setEditingClinic(null);
      clinicForm.resetFields();
      loadClinics();
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Ошибка сохранения клиники');
    }
  };

  const handleDeleteClinic = async (clinic: Clinic) => {
    try {
      await adminAPI.deleteClinic(clinic.id);
      message.success('Клиника удалена');
      loadClinics();
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Ошибка удаления клиники');
    }
  };

  const handleAddButterflies = async (values: any) => {
    try {
      await adminAPI.addButterflies(values.user_id, values.amount, values.description);
      message.success(`Успешно начислено ${values.amount} бабочек`);
      setButterflyModalVisible(false);
      butterflyForm.resetFields();
      await loadButterflyUsers();
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Ошибка начисления бабочек');
    }
  };

  const exportUsers = () => {
    const csv = [
      ['ID', 'Email', 'ФИО', 'Роль', 'Телефон', 'Статус'].join(','),
      ...users.map(u => [
        u.id,
        u.email,
        u.full_name,
        u.role,
        u.phone,
        u.is_active ? 'Активен' : 'Неактивен'
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Filtering is done on backend, but we can also filter here for immediate UI feedback
  const filteredUsers = users.filter(u => {
    if (roleFilter && u.role !== roleFilter) return false;
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (u.email && u.email.toLowerCase().includes(searchLower)) || 
             (u.full_name && u.full_name.toLowerCase().includes(searchLower));
    }
    return true;
  });

  const columns: ColumnsType<User> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Аватар',
      key: 'avatar',
      width: 80,
      render: (_, record) => (
        <Avatar 
          src={record.avatar_url ? getFilePreviewUrl(record.avatar_url) : undefined} 
          icon={<UserOutlined />}
          size="large"
        />
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'ФИО',
      dataIndex: 'full_name',
      key: 'full_name',
    },
    {
      title: 'DR-ID',
      key: 'doctor_public_id',
      width: 160,
      render: (_: unknown, record: User) =>
        record.role === 'doctor' ? (
          record.doctor_public_id ? (
            <Tag color="green">{record.doctor_public_id}</Tag>
          ) : (
            <Tag>нет ID</Tag>
          )
        ) : (
          '—'
        ),
    },
    {
      title: 'Роль',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => {
        const colors: Record<string, string> = {
          patient: 'blue',
          doctor: 'green',
          admin: 'red',
        };
        const labels: Record<string, string> = {
          patient: 'Пациент',
          doctor: 'Врач',
          admin: 'Администратор',
        };
        return <Tag color={colors[role]}>{labels[role]}</Tag>;
      },
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Telegram',
      dataIndex: 'telegram_username',
      key: 'telegram_username',
      render: (username: string) => username ? `@${username}` : '-',
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Активен' : 'Неактивен'}
        </Tag>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2} style={{ color: '#263238', marginBottom: 8 }}>Панель администратора</Title>
          <Text type="secondary">
            Управление пользователями системы
          </Text>
        </div>
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={exportUsers}
          >
            Экспорт CSV
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateUser}
            style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}
          >
            Создать пользователя
          </Button>
        </Space>
      </div>

      <Card style={{ borderRadius: '12px', marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Input
              placeholder="Поиск по email или ФИО"
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={6}>
            <Select
              placeholder="Фильтр по роли"
              style={{ width: '100%' }}
              value={roleFilter}
              onChange={setRoleFilter}
              allowClear
            >
              <Option value="patient">Пациент</Option>
              <Option value="doctor">Врач</Option>
              <Option value="admin">Администратор</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Text type="secondary">
              Найдено: {filteredUsers.length} пользователей
            </Text>
          </Col>
        </Row>
      </Card>

      <Card style={{ borderRadius: '12px' }}>
        <Tabs
          defaultActiveKey="users"
          items={[
            {
              key: 'users',
              label: 'Пользователи',
              children: (
                <>
                  <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#fff3cd', borderRadius: '8px' }}>
                    <ExclamationCircleOutlined style={{ color: '#856404', marginRight: 8 }} />
                    <Text strong style={{ color: '#856404' }}>
                      Внимание: Вы просматриваете конфиденциальные данные пользователей
                    </Text>
                  </div>
                  
                  <Table
                    columns={columns}
                    dataSource={filteredUsers}
                    loading={loading}
                    rowKey="id"
                    pagination={{ pageSize: 50 }}
                    scroll={{ x: 1200, y: 'calc(100vh - 300px)' }}
                    onRow={(record) => ({
                      onClick: (e) => {
                        // Prevent click if clicking on a button or link
                        const target = e.target as HTMLElement;
                        if (target.closest('button') || target.closest('a')) {
                          return;
                        }
                        handleRowClick(record);
                      },
                      style: { cursor: 'pointer' }
                    })}
                  />
                </>
              )
            },
            {
              key: 'clinics',
              label: 'Клиники',
              children: (
                <div>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    style={{ marginBottom: 16, backgroundColor: '#7CB342', borderColor: '#7CB342' }}
                    onClick={openCreateClinicModal}
                  >
                    Добавить клинику
                  </Button>
                  <Table
                    rowKey="id"
                    dataSource={clinics}
                    pagination={false}
                    columns={[
                      { title: 'ID', dataIndex: 'id', width: 60 },
                      { title: 'Название', dataIndex: 'name' },
                      { title: 'Город', dataIndex: 'city', render: (v: string) => v || '—' },
                      {
                        title: 'Код (slug)',
                        dataIndex: 'slug',
                        render: (slug: string) => <Tag>{slug}</Tag>,
                      },
                      {
                        title: 'Действия',
                        key: 'actions',
                        width: 160,
                        render: (_: any, clinic: Clinic) => (
                          <Space>
                            <Button
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => openEditClinicModal(clinic)}
                            >
                              Изменить
                            </Button>
                            <Popconfirm
                              title="Удалить клинику?"
                              description="У врачей этой клиники поле клиники будет очищено."
                              okText="Удалить"
                              cancelText="Отмена"
                              onConfirm={() => handleDeleteClinic(clinic)}
                            >
                              <Button size="small" danger icon={<DeleteOutlined />}>
                                Удалить
                              </Button>
                            </Popconfirm>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </div>
              ),
            },
            {
              key: 'butterflies',
              label: (
                <span>
                  <GiftOutlined />
                  Управление бабочками
                </span>
              ),
              children: (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setButterflyModalVisible(true);
                        loadButterflyUsers();
                      }}
                      style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}
                    >
                      Начислить бабочки
                    </Button>
                  </div>
                  
                  <Table
                    columns={[
                      {
                        title: 'ID',
                        dataIndex: 'id',
                        key: 'id',
                        width: 80,
                      },
                      {
                        title: 'ФИО',
                        dataIndex: 'full_name',
                        key: 'full_name',
                      },
                      {
                        title: 'Email',
                        dataIndex: 'email',
                        key: 'email',
                      },
                      {
                        title: 'Бабочки',
                        dataIndex: 'butterfly_balance',
                        key: 'butterfly_balance',
                        render: (balance: number) => (
                          <Tag color="gold" style={{ fontSize: '16px', padding: '4px 12px' }}>
                            {balance || 0} 🦋
                          </Tag>
                        ),
                      },
                    ]}
                    dataSource={butterflyUsers}
                    loading={loadingButterflyUsers}
                    rowKey="id"
                    pagination={{ pageSize: 50 }}
                  />
                </div>
              )
            }
          ]}
        />
      </Card>

      {/* Edit/Create User Modal */}
      <Modal
        title={editingUser ? 'Редактировать пользователя' : 'Создать пользователя'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="role"
            label="Роль"
            rules={[{ required: true }]}
          >
            <Select placeholder="Выберите роль" disabled={!!editingUser}>
              <Option value="patient">Пациент</Option>
              <Option value="doctor">Врач</Option>
              <Option value="admin">Администратор</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, type: 'email' }]}
          >
            <Input placeholder="email@example.com" disabled={!!editingUser} />
          </Form.Item>

          <Form.Item name="last_name" label="Фамилия" rules={[{ required: true }]}>
            <Input placeholder="Иванова" />
          </Form.Item>

          <Form.Item name="first_name" label="Имя" rules={[{ required: true }]}>
            <Input placeholder="Анна" />
          </Form.Item>

          <Form.Item name="middle_name" label="Отчество (если есть)">
            <Input placeholder="Сергеевна" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Телефон"
            rules={[{ required: true }]}
          >
            <Input placeholder="+7 (XXX) XXX-XX-XX" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Пароль"
            rules={[{ required: !editingUser, min: 6 }]}
          >
            <Input.Password placeholder={editingUser ? "Оставить без изменений (оставьте пустым)" : "Минимум 6 символов"} />
          </Form.Item>

          <Form.Item
            name="telegram_username"
            label="Telegram username"
          >
            <Input placeholder="username" prefix="@" />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.role !== currentValues.role}
          >
            {({ getFieldValue }) => {
              const role = getFieldValue('role');
              if (role === 'patient') {
                return (
                  <>
                    <Form.Item
                      name="week_of_pregnancy"
                      label="Срок беременности (неделя)"
                    >
                      <InputNumber min={1} max={42} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      name="due_date"
                      label="ПДР"
                    >
                      <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                    </Form.Item>
                  </>
                );
              }
              if (role === 'doctor') {
                return (
                  <>
                    <Form.Item
                      name="clinic_id"
                      label="Клиника"
                      rules={[{ required: true, message: 'Выберите клинику' }]}
                      tooltip="Код DR-ID строится из slug клиники (например GENERIC → DR-GENERIC-XXXXXX)"
                    >
                      <Select
                        placeholder="Выберите клинику"
                        showSearch
                        optionFilterProp="label"
                        options={clinics.map((c) => ({
                          value: c.id,
                          label: `${c.name} (${c.slug})${c.city ? ` — ${c.city}` : ''}`,
                        }))}
                      />
                    </Form.Item>
                    <Form.Item
                      name="specialization"
                      label="Специальность"
                    >
                      <Input placeholder="Акушер-гинеколог" />
                    </Form.Item>
                    <Form.Item
                      name="license_number"
                      label="Номер лицензии"
                    >
                      <Input placeholder="ЛО-XX-XX-XXXXXX" />
                    </Form.Item>
                  </>
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}>
                {editingUser ? 'Сохранить' : 'Создать'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>Отмена</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        title={`Сброс пароля для ${editingUser?.full_name || editingUser?.email}`}
        open={passwordModalVisible}
        onCancel={() => {
          setPasswordModalVisible(false);
          passwordForm.resetFields();
        }}
        footer={null}
        width={400}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordReset}
        >
          <Form.Item
            name="new_password"
            label="Новый пароль"
            rules={[{ required: true, min: 6, message: 'Минимум 6 символов' }]}
          >
            <Input.Password placeholder="Введите новый пароль" />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="Подтвердите пароль"
            dependencies={['new_password']}
            rules={[
              { required: true },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Пароли не совпадают'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Подтвердите новый пароль" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}>
                Изменить пароль
              </Button>
              <Button onClick={() => {
                setPasswordModalVisible(false);
                passwordForm.resetFields();
              }}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Assign Patient Modal */}
      <Modal
        title={`Назначить пациента врачу ${selectedDoctor?.full_name || selectedDoctor?.email}`}
        open={assignModalVisible}
        onCancel={() => {
          setAssignModalVisible(false);
          assignForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={assignForm}
          layout="vertical"
          onFinish={handleAssignSubmit}
        >
          <Form.Item
            name="patient_id"
            label="Выберите пациента"
            rules={[{ required: true, message: 'Выберите пациента' }]}
          >
            <Select
              placeholder="Выберите пациента"
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {users
                .filter(u => u.role === 'patient')
                .map(patient => {
                  const linkedDoctor = patient.doctor_id
                    ? users.find((d) => d.id === patient.doctor_id)
                    : null;
                  const label = linkedDoctor
                    ? `${patient.full_name} (${patient.email}) — врач: ${linkedDoctor.full_name || linkedDoctor.email}`
                    : `${patient.full_name} (${patient.email})`;
                  return (
                    <Option key={patient.id} value={patient.id}>
                      {label}
                    </Option>
                  );
                })}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}>
                Назначить
              </Button>
              <Button onClick={() => {
                setAssignModalVisible(false);
                assignForm.resetFields();
              }}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Doctor Patients Modal */}
      <Modal
        title={`Пациенты врача ${selectedDoctor?.full_name || selectedDoctor?.email}`}
        open={patientsModalVisible}
        onCancel={() => {
          setPatientsModalVisible(false);
          setSelectedDoctor(null);
          setDoctorPatients([]);
        }}
        footer={null}
        width={800}
      >
        <Table
          columns={[
            {
              title: 'ID',
              dataIndex: 'id',
              key: 'id',
              width: 80,
            },
            {
              title: 'ФИО',
              dataIndex: 'full_name',
              key: 'full_name',
            },
            {
              title: 'Email',
              dataIndex: 'email',
              key: 'email',
            },
            {
              title: 'Телефон',
              dataIndex: 'phone',
              key: 'phone',
            },
            {
              title: 'Действия',
              key: 'actions',
              render: (_, patient) => (
                <Space>
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => navigate(`/admin/patient/${patient.id}/diary`)}
                    size="small"
                  >
                    Дневник
                  </Button>
                  <Popconfirm
                    title="Удалить назначение?"
                    onConfirm={() => handleUnassignPatient(selectedDoctor!.id, patient.id)}
                    okText="Да"
                    cancelText="Нет"
                  >
                    <Button
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                      size="small"
                    >
                      Удалить назначение
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
          dataSource={doctorPatients}
          loading={loadingPatients}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Modal>

      {/* Butterfly Modal */}
      <Modal
        title="Начислить бабочки"
        open={butterflyModalVisible}
        onCancel={() => {
          setButterflyModalVisible(false);
          butterflyForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={butterflyForm} layout="vertical" onFinish={handleAddButterflies}>
          <Form.Item
            name="user_id"
            label="Пользователь"
            rules={[{ required: true, message: 'Выберите пользователя' }]}
          >
            <Select
              placeholder="Выберите пользователя"
              showSearch
              optionFilterProp="label"
              options={butterflyUsers.map((u: any) => ({
                value: u.id,
                label: `${u.full_name || u.email} (#${u.id})`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="amount"
            label="Количество бабочек"
            rules={[{ required: true, message: 'Введите количество' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label="Описание (опционально)">
            <Input placeholder="Например: бонус за активность" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}>
                Начислить
              </Button>
              <Button onClick={() => setButterflyModalVisible(false)}>Отмена</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Actions Modal - shows when user clicks on a row */}
      <Modal
        title={`Действия для пользователя: ${selectedUser?.full_name || selectedUser?.email || ''}`}
        open={actionsModalVisible}
        onCancel={() => {
          setActionsModalVisible(false);
          setSelectedUser(null);
        }}
        footer={null}
        width={600}
      >
        {selectedUser && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="ID">{selectedUser.id}</Descriptions.Item>
                <Descriptions.Item label="Email">{selectedUser.email}</Descriptions.Item>
                <Descriptions.Item label="ФИО">{selectedUser.full_name}</Descriptions.Item>
                <Descriptions.Item label="Телефон">{selectedUser.phone}</Descriptions.Item>
                <Descriptions.Item label="Роль">
                  <Tag color={selectedUser.role === 'patient' ? 'blue' : selectedUser.role === 'doctor' ? 'green' : 'red'}>
                    {selectedUser.role === 'patient' ? 'Пациент' : selectedUser.role === 'doctor' ? 'Врач' : 'Администратор'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Статус">
                  <Tag color={selectedUser.is_active ? 'green' : 'red'}>
                    {selectedUser.is_active ? 'Активен' : 'Неактивен'}
                  </Tag>
                </Descriptions.Item>
                {selectedUser.role === 'doctor' && (
                  <>
                    <Descriptions.Item label="Публичный ID">
                      {selectedUser.doctor_public_id || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Клиника (id)">
                      {selectedUser.clinic_id ?? '—'}
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>
            </Card>

            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => handleEditUser()}
                block
                size="large"
              >
                Редактировать профиль
              </Button>

              <Button
                icon={<KeyOutlined />}
                onClick={() => handleResetPassword()}
                block
                size="large"
              >
                Сбросить пароль
              </Button>

              {selectedUser.role === 'patient' && (
                <Button
                  icon={<EditOutlined />}
                  onClick={() => {
                    setActionsModalVisible(false);
                    navigate(`/admin/patient/${selectedUser.id}/diary`);
                  }}
                  block
                  size="large"
                >
                  Редактировать дневник
                </Button>
              )}

              {selectedUser.role === 'patient' && selectedUser.doctor_id && (
                <Popconfirm
                  title="Отвязать текущего врача?"
                  description="После отвязки можно назначить другого врача."
                  okText="Отвязать"
                  cancelText="Отмена"
                  onConfirm={() => handleForceUnlinkPatient(selectedUser.id)}
                >
                  <Button danger block size="large">
                    Отвязать врача
                  </Button>
                </Popconfirm>
              )}

              {selectedUser.role === 'doctor' && selectedUser.doctor_public_id && (
                <Card size="small" title="QR для привязки пациентов">
                  <DoctorPublicIdCard doctorPublicId={selectedUser.doctor_public_id} compact />
                </Card>
              )}

              {selectedUser.role === 'doctor' && (
                <>
                  <Button
                    icon={<TeamOutlined />}
                    onClick={() => {
                      setActionsModalVisible(false);
                      handleViewDoctorPatients(selectedUser);
                    }}
                    block
                    size="large"
                  >
                    Просмотреть пациентов врача
                  </Button>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setActionsModalVisible(false);
                      handleAssignPatient(selectedUser);
                    }}
                    block
                    size="large"
                  >
                    Назначить пациента
                  </Button>
                </>
              )}

              <Popconfirm
                title="Деактивировать пользователя?"
                description="Пользователь будет деактивирован, но не удален из системы"
                onConfirm={() => {
                  handleDeleteUser(selectedUser.id);
                  setActionsModalVisible(false);
                  setSelectedUser(null);
                }}
                okText="Да"
                cancelText="Нет"
                okButtonProps={{ danger: true }}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  block
                  size="large"
                >
                  Удалить (деактивировать)
                </Button>
              </Popconfirm>
            </Space>
          </Space>
        )}
      </Modal>

      <Modal
        title={editingClinic ? `Редактировать клинику #${editingClinic.id}` : 'Новая клиника'}
        open={clinicModalVisible}
        onCancel={() => {
          setClinicModalVisible(false);
          setEditingClinic(null);
          clinicForm.resetFields();
        }}
        footer={null}
        width={480}
      >
        <Form form={clinicForm} layout="vertical" onFinish={handleSaveClinic}>
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: 'Укажите название' }]}
          >
            <Input placeholder="Клиника на Невском" />
          </Form.Item>
          <Form.Item
            name="slug"
            label="Код клиники (slug)"
            rules={[
              ...(editingClinic
                ? [{ required: true, message: 'Укажите код' } as const]
                : []),
              { pattern: /^[A-Za-z0-9]{0,12}$/, message: 'До 12 латинских букв/цифр' },
              ...(editingClinic
                ? [{ pattern: /^[A-Za-z0-9]{2,12}$/, message: '2–12 латинских букв/цифр' } as const]
                : []),
            ]}
            tooltip={
              editingClinic
                ? 'Код используется в DR-ID: DR-SLUG-XXXXXX'
                : 'Необязательно. Если не указать — код сформируется автоматически. Используется в DR-ID: DR-SLUG-XXXXXX'
            }
          >
            <Input
              placeholder={editingClinic ? 'SPB01' : 'Автоматически, или например SPB01'}
              style={{ textTransform: 'uppercase' }}
            />
          </Form.Item>
          <Form.Item name="city" label="Город">
            <Input placeholder="Санкт-Петербург" />
          </Form.Item>
          <Form.Item name="contact_phone" label="Телефон">
            <Input placeholder="+7 ..." />
          </Form.Item>
          <Form.Item name="contact_email" label="Email">
            <Input placeholder="clinic@example.com" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>
            {editingClinic ? 'Сохранить' : 'Создать'}
          </Button>
        </Form>
      </Modal>
    </div>
  );
};
